import type { OverallTier } from '@/domains/results/types';

export interface ScoreGaugeProps {
  score: number; // 0 a 100
  tier: OverallTier;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function ScoreGauge({
  score,
  tier,
  size = 'md',
  className = '',
}: ScoreGaugeProps) {
  const clampedScore = Math.max(0, Math.min(100, Math.round(score)));

  const sizeMap = {
    sm: { diameter: 120, stroke: 10, fontSize: 'text-2xl', labelSize: 'text-xs' },
    md: { diameter: 160, stroke: 12, fontSize: 'text-4xl', labelSize: 'text-sm' },
    lg: { diameter: 200, stroke: 14, fontSize: 'text-5xl', labelSize: 'text-base' },
  };

  const { diameter, stroke, fontSize, labelSize } = sizeMap[size];
  const radius = (diameter - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (clampedScore / 100) * circumference;

  // Cor do anel baseada no tier
  const getTierColor = (key: string): string => {
    switch (key) {
      case 'structured':
        return '#2E8B57'; // success
      case 'conversations':
      case 'building':
        return '#FE591F'; // orange
      case 'scattered':
        return '#C67A19'; // warning
      case 'housekeeping':
      default:
        return '#B83A3A'; // danger
    }
  };

  const ringColor = getTierColor(tier.key);

  return (
    <div
      role="meter"
      aria-valuenow={clampedScore}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Nota Geral: ${clampedScore} de 100. Faixa de maturidade: ${tier.label}`}
      className={`relative inline-flex flex-col items-center justify-center ${className}`}
    >
      <svg
        width={diameter}
        height={diameter}
        className="-rotate-90 transform"
        aria-hidden="true"
      >
        {/* Círculo de fundo */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={stroke}
          fill="none"
        />
        {/* Anel de progresso */}
        <circle
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          stroke={ringColor}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="none"
          className="transition-all duration-1000 ease-out motion-reduce:transition-none"
        />
      </svg>

      {/* Rótulo central */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className={`font-black tracking-tight text-white ${fontSize}`}>
          {clampedScore}
        </span>
        <span className="text-[10px] uppercase font-bold tracking-wider text-brand-offWhite/60">
          de 100
        </span>
      </div>

      <div className="mt-3 text-center">
        <span
          className={`inline-block rounded-full border px-3 py-1 font-semibold text-white ${labelSize}`}
          style={{ borderColor: `${ringColor}60`, backgroundColor: `${ringColor}20` }}
        >
          {tier.label}
        </span>
      </div>
    </div>
  );
}
